import { CheckSquare, X, Clock, Calendar, UserCheck, Briefcase, DollarSign, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

interface NotificationListProps {
  notifications: any[];
  onNotificationClick: (notification: any) => void;
  onDeleteNotification: (id: string) => void;
}

export const NotificationList = ({
  notifications,
  onNotificationClick,
  onDeleteNotification,
}: NotificationListProps) => {
  const getIcon = (type: string) => {
    switch (type) {
      case 'aufgabe_assigned':
      case 'aufgabe_completed':
        return <CheckSquare className="h-4 w-4" />;
      case 'aufgabe_due':
      case 'aufgabe_overdue':
        return <Clock className="h-4 w-4" />;
      case 'termin_reminder':
      case 'termin_today':
        return <Calendar className="h-4 w-4" />;
      case 'lead_assigned':
      case 'lead_status_changed':
        return <UserCheck className="h-4 w-4" />;
      case 'lead_wiedervorlage':
        return <Briefcase className="h-4 w-4" />;
      case 'abrechnung_new':
        return <DollarSign className="h-4 w-4" />;
      case 'vertrag_expiring':
        return <FileText className="h-4 w-4" />;
      default:
        return <CheckSquare className="h-4 w-4" />;
    }
  };

  const getIconColor = (type: string) => {
    switch (type) {
      case 'aufgabe_assigned':
        return 'text-chart-2';
      case 'aufgabe_completed':
        return 'text-chart-5';
      case 'aufgabe_due':
        return 'text-primary';
      case 'aufgabe_overdue':
        return 'text-destructive';
      case 'termin_reminder':
      case 'termin_today':
        return 'text-chart-4';
      case 'lead_assigned':
      case 'lead_status_changed':
        return 'text-chart-2';
      case 'lead_wiedervorlage':
        return 'text-chart-1';
      case 'abrechnung_new':
        return 'text-chart-5';
      case 'vertrag_expiring':
        return 'text-primary';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="divide-y">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={cn(
            "group relative p-4 hover:bg-muted/50 transition-colors cursor-pointer",
            !notification.read && "bg-muted/30"
          )}
          onClick={() => onNotificationClick(notification)}
        >
          {/* Unread indicator */}
          {!notification.read && (
            <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary" />
          )}

          <div className="flex gap-3 items-start pl-4">
            {/* Icon */}
            <div className={cn("mt-0.5", getIconColor(notification.type))}>
              {getIcon(notification.type)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className={cn(
                    "text-sm font-medium",
                    !notification.read && "font-semibold"
                  )}>
                    {notification.title}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {notification.message}
                  </p>
                </div>

                {/* Delete button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteNotification(notification.id);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Timestamp */}
              <p className="text-xs text-muted-foreground mt-2">
                {formatDistanceToNow(new Date(notification.created_at), {
                  addSuffix: true,
                  locale: de,
                })}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};